// src/pages/CadastroScreen.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  BrainCircuit,
  Check,
  CheckCircle2,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";

import "../sass/pages/cadastro.scss";
import Logo from "../assets/logoClaro.webp";
import { API_BASE_URL } from "../config/api";
import { useToast } from "../context/ToastContext";

const ABORDAGENS = [
  {
    value: "",
    label: "Selecione sua abordagem",
  },
  {
    value: "Análise do Comportamento",
    label: "Análise do Comportamento",
  },
  {
    value: "Terapia Cognitivo-Comportamental (TCC)",
    label: "Terapia Cognitivo-Comportamental (TCC)",
  },
  {
    value: "Fenomenologia Existencial",
    label: "Fenomenologia Existencial",
  },
  {
    value: "Psicanálise",
    label: "Psicanálise",
  },
  {
    value: "Humanista",
    label: "Humanista",
  },
  {
    value: "Sistêmica",
    label: "Sistêmica",
  },
  {
    value: "Outra",
    label: "Outra",
  },
];

const STORAGE_KEYS = {
  TEMP_SESSION_ID: "psy_temp_register_session_id",
  TEMP_EMAIL: "psy_temp_register_email",
  PLAN_NAME: "psy_temp_register_plan_name",
  PLAN_PRICE: "psy_temp_register_plan_price",
  TIPO_USUARIO: "psy_temp_register_tipo_usuario",
  PRICE_ID: "psy_temp_register_price_id",
  REGISTRATION_FLOW: "psyassist:registration-flow",
  SELECTED_PLAN: "psyassist:selected-plan",
};

const EMPTY_FLOW = {
  sessionId: "",
  email: "",
  planName: "Plano não identificado",
  planPriceIndividual: "Valor não informado",
  planPriceClinica: "Valor não informado",
  tipoUsuario: "individual",
  priceId: "",
};

const safeParseJson = (value, fallback = null) => {
  if (!value) return fallback;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const readStorage = (storage, key) => {
  try {
    return storage.getItem(key) || "";
  } catch {
    return "";
  }
};

const writeStorage = (storage, key, value) => {
  try {
    storage.setItem(key, String(value ?? ""));
    return true;
  } catch {
    return false;
  }
};

const isEmailValid = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email || "").trim());

const getApiErrorMessage = (
  error,
  fallback = "Não foi possível concluir a operação."
) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const normalizeFlow = (source = {}) => {
  const tipoUsuario =
    source.tipoUsuario === "clinica" ? "clinica" : "individual";

  return {
    sessionId: String(source.sessionId || "").trim(),
    email: String(source.email || "").trim(),
    planName: String(
      source.planName ||
        source.plano ||
        EMPTY_FLOW.planName
    ).trim(),
    planPriceIndividual: String(
      source.planPriceIndividual ||
        source.planPrice ||
        source.valorPlano ||
        EMPTY_FLOW.planPriceIndividual
    ).trim(),
    planPriceClinica: String(
      source.planPriceClinica ||
        source.planPriceIndividual ||
        source.planPrice ||
        source.valorPlano ||
        EMPTY_FLOW.planPriceClinica
    ).trim(),
    tipoUsuario,
    priceId: String(source.priceId || "").trim(),
  };
};

const getPasswordRules = (value) => [
  {
    key: "length",
    label: "Pelo menos 8 caracteres",
    valid: value.length >= 8,
  },
  {
    key: "letter",
    label: "Ao menos uma letra",
    valid: /[A-Za-zÀ-ÿ]/.test(value),
  },
  {
    key: "number",
    label: "Ao menos um número",
    valid: /\d/.test(value),
  },
];

function FormField({
  id,
  label,
  hint,
  error,
  required = false,
  children,
}) {
  return (
    <div className={`cadastro-field ${error ? "cadastro-field--error" : ""}`}>
      <div className="cadastro-field__header">
        <label htmlFor={id}>
          {label}
          {required ? <span aria-hidden="true">*</span> : null}
        </label>

        {hint ? <small>{hint}</small> : null}
      </div>

      {children}

      {error ? (
        <p className="cadastro-field__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

const CadastroScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [abordagemSelecionada, setAbordagemSelecionada] = useState("");

  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmarSenha, setMostrarConfirmarSenha] = useState(false);

  const [tipoUsuario, setTipoUsuario] = useState("individual");
  const [planName, setPlanName] = useState(EMPTY_FLOW.planName);
  const [planPriceIndividual, setPlanPriceIndividual] = useState(
    EMPTY_FLOW.planPriceIndividual
  );
  const [planPriceClinica, setPlanPriceClinica] = useState(
    EMPTY_FLOW.planPriceClinica
  );
  const [sessionId, setSessionId] = useState("");
  const [priceId, setPriceId] = useState("");

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  useEffect(() => {
    const navigationState =
      location.state && typeof location.state === "object"
        ? location.state
        : {};

    const sessionFlow =
      safeParseJson(
        readStorage(sessionStorage, STORAGE_KEYS.REGISTRATION_FLOW),
        null
      ) ||
      safeParseJson(
        readStorage(sessionStorage, STORAGE_KEYS.SELECTED_PLAN),
        null
      ) ||
      {};

    const legacyFlow = {
      sessionId: readStorage(localStorage, STORAGE_KEYS.TEMP_SESSION_ID),
      email: readStorage(localStorage, STORAGE_KEYS.TEMP_EMAIL),
      planName: readStorage(localStorage, STORAGE_KEYS.PLAN_NAME),
      planPriceIndividual: readStorage(
        localStorage,
        STORAGE_KEYS.PLAN_PRICE
      ),
      tipoUsuario: readStorage(localStorage, STORAGE_KEYS.TIPO_USUARIO),
      priceId: readStorage(localStorage, STORAGE_KEYS.PRICE_ID),
    };

    const source = {
      ...legacyFlow,
      ...sessionFlow,
      ...navigationState,
    };

    const normalized = normalizeFlow(source);

    setSessionId(normalized.sessionId);
    setEmail(normalized.email);
    setPlanName(normalized.planName);
    setPlanPriceIndividual(normalized.planPriceIndividual);
    setPlanPriceClinica(normalized.planPriceClinica);
    setTipoUsuario(normalized.tipoUsuario);
    setPriceId(normalized.priceId);

    writeStorage(
      localStorage,
      STORAGE_KEYS.TEMP_SESSION_ID,
      normalized.sessionId
    );
    writeStorage(localStorage, STORAGE_KEYS.TEMP_EMAIL, normalized.email);
    writeStorage(localStorage, STORAGE_KEYS.PLAN_NAME, normalized.planName);
    writeStorage(
      localStorage,
      STORAGE_KEYS.PLAN_PRICE,
      normalized.tipoUsuario === "clinica"
        ? normalized.planPriceClinica
        : normalized.planPriceIndividual
    );
    writeStorage(
      localStorage,
      STORAGE_KEYS.TIPO_USUARIO,
      normalized.tipoUsuario
    );
    writeStorage(localStorage, STORAGE_KEYS.PRICE_ID, normalized.priceId);
  }, [location.state]);

  const displayedPlanPrice = useMemo(
    () =>
      tipoUsuario === "clinica"
        ? planPriceClinica
        : planPriceIndividual,
    [planPriceClinica, planPriceIndividual, tipoUsuario]
  );

  const userTypeLabel =
    tipoUsuario === "clinica" ? "Conta clínica" : "Conta profissional";

  const passwordRules = useMemo(() => getPasswordRules(senha), [senha]);

  const passwordScore = useMemo(
    () => passwordRules.filter((rule) => rule.valid).length,
    [passwordRules]
  );

  const passwordStrengthLabel = useMemo(() => {
    if (!senha) return "Ainda não informada";
    if (passwordScore <= 1) return "Fraca";
    if (passwordScore === 2) return "Boa";
    return "Forte";
  }, [passwordScore, senha]);

  const clearFieldError = (field) => {
    setErrors((current) => ({
      ...current,
      [field]: "",
    }));
  };

  const verificarEmailExiste = async (emailCheck) => {
    setIsCheckingEmail(true);

    try {
      await axios.get(`${API_BASE_URL}/psy/check-email`, {
        params: {
          email: emailCheck,
        },
      });

      return false;
    } catch (error) {
      if (error?.response?.status === 409) {
        return true;
      }

      console.warn("Não foi possível validar o e-mail previamente:", {
        status: error?.response?.status,
        data: error?.response?.data,
      });

      return false;
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const montarPayloadCadastroTemporario = (sessionIdAlvo) => ({
    sessionId: sessionIdAlvo,
    email: email.trim().toLowerCase(),
    senha,
    abordagem: abordagemSelecionada || null,
    tipoUsuario,
    plano: planName,
    valorPlano: displayedPlanPrice,
    priceId,
  });

  const atualizarCadastroTemporario = async ({
    sessionIdAlvo,
    permiteReaproveitarSessao = true,
  }) => {
    if (!sessionIdAlvo) {
      toast.error({
        title: "Sessão de cadastro inválida",
        message:
          "Volte à seleção de planos e reinicie o processo de contratação.",
        duration: 0,
      });

      return {
        success: false,
        sessionIdFinal: "",
      };
    }

    const url = `${API_BASE_URL}/psy/temp-register/${sessionIdAlvo}`;
    const body = montarPayloadCadastroTemporario(sessionIdAlvo);

    try {
      const response = await axios.put(url, body, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const sessionIdFinal =
        response?.data?.data?.sessionId ||
        response?.data?.sessionId ||
        sessionIdAlvo;

      writeStorage(
        localStorage,
        STORAGE_KEYS.TEMP_SESSION_ID,
        sessionIdFinal
      );
      writeStorage(
        localStorage,
        STORAGE_KEYS.TEMP_EMAIL,
        email.trim().toLowerCase()
      );

      setSessionId(sessionIdFinal);

      return {
        success: response.status === 200 || response.status === 201,
        sessionIdFinal,
      };
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const normalizedMessage = String(data?.message || "").toLowerCase();

      console.error("Erro na atualização do cadastro temporário:", {
        status,
        data,
        message: error?.message,
      });

      if (status === 409) {
        const emailAlreadyRegistered =
          normalizedMessage.includes("e-mail já cadastrado") ||
          normalizedMessage.includes("email já cadastrado");

        if (emailAlreadyRegistered) {
          setErrors((current) => ({
            ...current,
            email: "Este e-mail já está vinculado a uma conta.",
          }));

          toast.error({
            title: "E-mail já cadastrado",
            message:
              "Entre na sua conta existente ou utilize outro endereço de e-mail.",
          });

          return {
            success: false,
            sessionIdFinal: sessionIdAlvo,
          };
        }

        const sessionIdExistente =
          data?.sessionIdExistente ||
          data?.sessionId ||
          data?.data?.sessionId ||
          "";

        if (sessionIdExistente && permiteReaproveitarSessao) {
          writeStorage(
            localStorage,
            STORAGE_KEYS.TEMP_SESSION_ID,
            sessionIdExistente
          );
          writeStorage(
            localStorage,
            STORAGE_KEYS.TEMP_EMAIL,
            email.trim().toLowerCase()
          );

          setSessionId(sessionIdExistente);

          toast.info({
            title: "Cadastro recuperado",
            message:
              "Localizamos um cadastro em andamento e retomamos o processo.",
          });

          return atualizarCadastroTemporario({
            sessionIdAlvo: sessionIdExistente,
            permiteReaproveitarSessao: false,
          });
        }

        toast.warning({
          title: "Cadastro já iniciado",
          message:
            data?.message ||
            "Já existe um cadastro em andamento para este e-mail.",
          duration: 0,
        });

        return {
          success: false,
          sessionIdFinal: sessionIdAlvo,
        };
      }

      toast.error({
        title: "Não foi possível salvar seus dados",
        message: getApiErrorMessage(
          error,
          "O servidor não concluiu a atualização do cadastro."
        ),
      });

      return {
        success: false,
        sessionIdFinal: sessionIdAlvo,
      };
    }
  };

  const validarCampos = () => {
    const nextErrors = {};
    const emailTrim = email.trim();

    if (!emailTrim) {
      nextErrors.email = "Informe seu e-mail profissional.";
    } else if (!isEmailValid(emailTrim)) {
      nextErrors.email = "Informe um endereço de e-mail válido.";
    }

    if (!senha) {
      nextErrors.senha = "Crie uma senha para sua conta.";
    } else if (!passwordRules.every((rule) => rule.valid)) {
      nextErrors.senha = "A senha ainda não atende aos requisitos mínimos.";
    }

    if (!confirmarSenha) {
      nextErrors.confirmarSenha = "Confirme a senha informada.";
    } else if (senha !== confirmarSenha) {
      nextErrors.confirmarSenha = "As senhas não coincidem.";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      toast.warning({
        title: "Revise os dados",
        message: "Existem campos que precisam da sua atenção.",
      });

      window.setTimeout(() => {
        const firstInvalidField = document.querySelector(
          ".cadastro-field--error input, .cadastro-field--error select"
        );

        firstInvalidField?.focus();
        firstInvalidField?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 80);

      return false;
    }

    return true;
  };

  const persistirFluxoLocal = ({ sessionIdFinal, emailTrim }) => {
    const flow = {
      sessionId: sessionIdFinal,
      email: emailTrim,
      planName,
      planPriceIndividual,
      planPriceClinica,
      tipoUsuario,
      priceId,
    };

    writeStorage(localStorage, "email", emailTrim);
    writeStorage(localStorage, STORAGE_KEYS.TEMP_EMAIL, emailTrim);
    writeStorage(
      localStorage,
      STORAGE_KEYS.TEMP_SESSION_ID,
      sessionIdFinal
    );
    writeStorage(localStorage, STORAGE_KEYS.PLAN_NAME, planName);
    writeStorage(
      localStorage,
      STORAGE_KEYS.PLAN_PRICE,
      displayedPlanPrice
    );
    writeStorage(localStorage, STORAGE_KEYS.TIPO_USUARIO, tipoUsuario);
    writeStorage(localStorage, STORAGE_KEYS.PRICE_ID, priceId);

    try {
      sessionStorage.setItem(
        STORAGE_KEYS.REGISTRATION_FLOW,
        JSON.stringify(flow)
      );
    } catch {
      // O state de navegação mantém o fluxo mesmo sem sessionStorage.
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isLoading) return;
    if (!validarCampos()) return;

    const emailTrim = email.trim().toLowerCase();

    setIsLoading(true);

    try {
      const emailExiste = await verificarEmailExiste(emailTrim);

      if (emailExiste) {
        setErrors((current) => ({
          ...current,
          email: "Este e-mail já está vinculado a uma conta.",
        }));

        toast.error({
          title: "E-mail já cadastrado",
          message:
            "Faça login com sua conta existente ou utilize outro endereço.",
        });

        return;
      }

      const resultado = await atualizarCadastroTemporario({
        sessionIdAlvo: sessionId,
        permiteReaproveitarSessao: true,
      });

      if (!resultado.success) return;

      const sessionIdFinal = resultado.sessionIdFinal || sessionId;

      persistirFluxoLocal({
        sessionIdFinal,
        emailTrim,
      });

      navigate("/cadastro-detalhes", {
        state: {
          sessionId: sessionIdFinal,
          planName,
          planPrice: displayedPlanPrice,
          planPriceIndividual,
          planPriceClinica,
          tipoUsuario,
          priceId,
          email: emailTrim,
        },
      });
    } catch (error) {
      console.error("Erro inesperado no cadastro:", error);

      toast.error({
        title: "Não foi possível avançar",
        message:
          "Ocorreu uma falha inesperada. Verifique sua conexão e tente novamente.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="cadastro-page">
      <div className="cadastro-background" aria-hidden="true">
        <span className="cadastro-background__orb cadastro-background__orb--one" />
        <span className="cadastro-background__orb cadastro-background__orb--two" />
        <span className="cadastro-background__grid" />
      </div>

      <div className="cadastro-layout">
        <aside className="cadastro-aside">
          <div className="cadastro-aside__brand">
            <img src={Logo} alt="PsyAssist Pro" />

            <div>
              <strong>PsyAssist Pro</strong>
              <span>Configuração inteligente da sua conta</span>
            </div>
          </div>

          <div className="cadastro-aside__content">
            <span className="cadastro-kicker">
              <Sparkles size={15} />
              Onboarding assistido
            </span>

            <h1>Sua rotina começa organizada desde o primeiro acesso.</h1>

            <p>
              Configure os dados essenciais da conta. A PSYA utilizará esse
              contexto para personalizar sua experiência na plataforma.
            </p>

            <div className="cadastro-benefits">
              <div className="cadastro-benefit">
                <span>
                  <BrainCircuit size={19} />
                </span>

                <div>
                  <strong>Experiência personalizada</strong>
                  <small>
                    A plataforma adapta recomendações e fluxos ao seu perfil.
                  </small>
                </div>
              </div>

              <div className="cadastro-benefit">
                <span>
                  <ShieldCheck size={19} />
                </span>

                <div>
                  <strong>Cadastro seguro</strong>
                  <small>
                    Seus dados são tratados dentro do fluxo protegido da conta.
                  </small>
                </div>
              </div>
            </div>
          </div>

          <div className="cadastro-plan-card">
            <div className="cadastro-plan-card__top">
              <span>{userTypeLabel}</span>
              <CheckCircle2 size={18} />
            </div>

            <strong>{planName}</strong>
            <p>{displayedPlanPrice}</p>

            <small>
              Plano confirmado na etapa anterior. Você poderá gerenciar a
              assinatura após concluir o cadastro.
            </small>
          </div>
        </aside>

        <section className="cadastro-panel">
          <div className="cadastro-progress">
            <div className="cadastro-progress__header">
              <span>Etapa 2 de 3</span>
              <strong>Dados de acesso</strong>
            </div>

            <div
              className="cadastro-progress__track"
              role="progressbar"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow="66"
              aria-label="Progresso do cadastro"
            >
              <span style={{ width: "66%" }} />
            </div>
          </div>

          <header className="cadastro-header">
            <div className="cadastro-header__icon" aria-hidden="true">
              <UserRound size={22} />
            </div>

            <div>
              <span>Criação da conta</span>
              <h2>Defina seus dados de acesso</h2>
              <p>
                Use um e-mail profissional e crie uma senha segura. Esses dados
                serão utilizados para acessar o PsyAssist Pro.
              </p>
            </div>
          </header>

          <form className="cadastro-form" onSubmit={handleSubmit} noValidate>
            <FormField
              id="cadastro-email"
              label="E-mail profissional"
              hint={isCheckingEmail ? "Validando..." : "Será usado no acesso"}
              error={errors.email}
              required
            >
              <div className="cadastro-control">
                <Mail size={18} aria-hidden="true" />

                <input
                  id="cadastro-email"
                  type="email"
                  placeholder="nome@clinica.com.br"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    clearFieldError("email");
                  }}
                  autoComplete="email"
                  disabled={isLoading}
                  aria-invalid={Boolean(errors.email)}
                />
              </div>
            </FormField>

            <div className="cadastro-form__row">
              <FormField
                id="cadastro-senha"
                label="Senha"
                error={errors.senha}
                required
              >
                <div className="cadastro-control">
                  <LockKeyhole size={18} aria-hidden="true" />

                  <input
                    id="cadastro-senha"
                    type={mostrarSenha ? "text" : "password"}
                    placeholder="Crie uma senha segura"
                    value={senha}
                    onChange={(event) => {
                      setSenha(event.target.value);
                      clearFieldError("senha");
                    }}
                    autoComplete="new-password"
                    disabled={isLoading}
                    aria-invalid={Boolean(errors.senha)}
                  />

                  <button
                    type="button"
                    className="cadastro-password-toggle"
                    onClick={() => setMostrarSenha((current) => !current)}
                    aria-label={mostrarSenha ? "Ocultar senha" : "Exibir senha"}
                    disabled={isLoading}
                  >
                    {mostrarSenha ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </FormField>

              <FormField
                id="cadastro-confirmar-senha"
                label="Confirmar senha"
                error={errors.confirmarSenha}
                required
              >
                <div className="cadastro-control">
                  <LockKeyhole size={18} aria-hidden="true" />

                  <input
                    id="cadastro-confirmar-senha"
                    type={mostrarConfirmarSenha ? "text" : "password"}
                    placeholder="Repita a senha"
                    value={confirmarSenha}
                    onChange={(event) => {
                      setConfirmarSenha(event.target.value);
                      clearFieldError("confirmarSenha");
                    }}
                    autoComplete="new-password"
                    disabled={isLoading}
                    aria-invalid={Boolean(errors.confirmarSenha)}
                  />

                  <button
                    type="button"
                    className="cadastro-password-toggle"
                    onClick={() =>
                      setMostrarConfirmarSenha((current) => !current)
                    }
                    aria-label={
                      mostrarConfirmarSenha
                        ? "Ocultar confirmação de senha"
                        : "Exibir confirmação de senha"
                    }
                    disabled={isLoading}
                  >
                    {mostrarConfirmarSenha ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
              </FormField>
            </div>

            <div className="cadastro-password-panel">
              <div className="cadastro-password-panel__header">
                <span>Segurança da senha</span>

                <strong
                  className={`cadastro-password-panel__strength cadastro-password-panel__strength--${passwordScore}`}
                >
                  {passwordStrengthLabel}
                </strong>
              </div>

              <div className="cadastro-password-panel__meter">
                {[1, 2, 3].map((step) => (
                  <span
                    key={step}
                    className={passwordScore >= step ? "is-active" : ""}
                  />
                ))}
              </div>

              <div className="cadastro-password-panel__rules">
                {passwordRules.map((rule) => (
                  <span
                    key={rule.key}
                    className={rule.valid ? "is-valid" : ""}
                  >
                    <Check size={14} />
                    {rule.label}
                  </span>
                ))}
              </div>
            </div>

            <FormField
              id="cadastro-abordagem"
              label="Abordagem profissional"
              hint="Opcional"
            >
              <div className="cadastro-control cadastro-control--select">
                <BrainCircuit size={18} aria-hidden="true" />

                <select
                  id="cadastro-abordagem"
                  value={abordagemSelecionada}
                  onChange={(event) =>
                    setAbordagemSelecionada(event.target.value)
                  }
                  disabled={isLoading}
                >
                  {ABORDAGENS.map((abordagem) => (
                    <option
                      key={abordagem.value || "empty"}
                      value={abordagem.value}
                    >
                      {abordagem.label}
                    </option>
                  ))}
                </select>
              </div>

              <p className="cadastro-field__support">
                Esse dado ajuda a PSYA a adaptar sugestões e contexto sem
                interferir no seu julgamento profissional.
              </p>
            </FormField>

            <div className="cadastro-security-note">
              <ShieldCheck size={19} />

              <p>
                <strong>Seus dados de acesso são privados.</strong>
                Não compartilhamos sua senha e não a exibimos posteriormente.
              </p>
            </div>

            <div className="cadastro-actions">
              <button
                type="button"
                className="cadastro-secondary-btn"
                onClick={() => navigate(-1)}
                disabled={isLoading}
              >
                <ArrowLeft size={18} />
                Voltar
              </button>

              <button
                type="submit"
                className="cadastro-primary-btn"
                disabled={isLoading || isCheckingEmail}
              >
                <span>
                  {isLoading ? "Salvando seus dados..." : "Continuar cadastro"}
                </span>

                {!isLoading ? <ArrowRight size={18} /> : null}
              </button>
            </div>
          </form>

          <footer className="cadastro-footer">
            <span>Ambiente seguro</span>
            <span aria-hidden="true">•</span>
            <span>Dados protegidos</span>
            <span aria-hidden="true">•</span>
            <span>Suporte PsyAssist</span>
          </footer>
        </section>
      </div>
    </main>
  );
};

export default CadastroScreen;
